import React, { useEffect, useState } from "react";
import { Card, Image, Text, Group, Badge } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { Link } from "react-router-dom";
import { SectionTitle } from "../common/SectionTitle";
import { categoryService, Category } from "../../services/categoryService";
import { galleryService } from "../../services/galleryService";
import { Loader } from "../common/Loader";

const API_URL =
  import.meta.env.VITE_API_BASE_URL ||
  "https://thalvora-exports.onrender.com";

export const OurProducts: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryImageMap, setCategoryImageMap] = useState<Record<string, string>>({});

  const scrollToTop = () => window.scrollTo(0, 0);

  useEffect(() => {
    const loadData = async () => {
      try {
        // 🔥 STEP 1: Check cache (BIGGEST SPEED BOOST)
        const cachedCats = sessionStorage.getItem("categories");
        const cachedImages = sessionStorage.getItem("categoryImages");

        if (cachedCats && cachedImages) {
          setCategories(JSON.parse(cachedCats));
          setCategoryImageMap(JSON.parse(cachedImages));
          setLoading(false);
          return;
        }

        // 🔥 STEP 2: Parallel API calls
        const [cats, gallery] = await Promise.all([
          categoryService.getAllCategories(),
          galleryService.getPublicGallery("PRODUCTS"),
        ]);

        const sortedCats = [...cats].sort(
  (a, b) => (a.displayOrder  || 0) - (b.displayOrder  || 0)
);
        setCategories(sortedCats);


        const activeImages = gallery.items.filter(
          (item) => item.type === "IMAGE" && item.active
        );

        const imageMap: Record<string, string> = {};

        activeImages.forEach((item) => {
          const title = item.title?.trim().toLowerCase();
          if (!title) return;

          const matchedCat = sortedCats.find(
            (c) => c.name.toLowerCase() === title
          );

          if (matchedCat) {
            const fullUrl = item.imageUrl.startsWith("http")
              ? item.imageUrl
              : `${API_URL}${item.imageUrl}`;

            imageMap[matchedCat.id] = fullUrl;
          }
        });

        setCategoryImageMap(imageMap);

        // 🔥 STEP 3: Save to cache
        sessionStorage.setItem("categories", JSON.stringify(sortedCats));
        sessionStorage.setItem("categoryImages", JSON.stringify(imageMap));

      } catch (error) {
        console.error("Error loading categories/gallery:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <div>
      <SectionTitle
        title="Our Products"
        subtitle="Explore our wide range of product categories"
      />

      {/* 🔥 Non-blocking loader */}
      {loading && (
        <div className="flex justify-center items-center py-6">
          <Loader />
        </div>
      )}

      {!loading && categories.length === 0 && (
        <Text ta="center" mt="lg">
          No categories available
        </Text>
      )}

      {!loading && categories.length > 0 && (
        <Carousel
          slideSize={{ base: "100%", sm: "50%", md: "33.33%", lg: "20%" }}
          slideGap="md"
          align="start"
          withIndicators={categories.length > 3}
          loop
          controlSize={28}
        >
          {categories.map((category) => {
            const imageUrl =
              categoryImageMap[category.id] || category.imageUrl;

            const finalImage =
              imageUrl && imageUrl.startsWith("http")
                ? imageUrl
                : imageUrl
                ? `${API_URL}${imageUrl}`
                : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

            return (
              <Carousel.Slide key={category.id}>
                <Card
                  component={Link}
                  to={`/products?category=${category.id}`}
                  onClick={scrollToTop}
                  padding="lg"
                  radius="lg"
                  withBorder
                  className="hover:transform hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                  style={{ height: "100%" }}
                >
                  <Card.Section>
                    <Image
                      src={finalImage}
                      height={160}
                      alt={category.name}
                      fit="cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </Card.Section>

                  <Group justify="space-between" mt="md" mb="xs">
                    <Text fw={600} size="lg">
                      {category.name}
                    </Text>
                    <Badge color="exportGreen" variant="light">
                      {category.productCount || 0}+
                    </Badge>
                  </Group>

                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {category.description || "No description available"}
                  </Text>
                </Card>
              </Carousel.Slide>
            );
          })}
        </Carousel>
      )}
    </div>
  );
};